import { useState } from 'react';
import { Camera, Check, Image, Images, Loader2, Lock, LogIn, MessageSquarePlus, Undo2, X } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import {
  useMyChecklistStore,
  openBlockerFor,
  CHECKLIST_NOTE_MAX,
  type ChecklistItemState,
  type TaskChecklistGroup,
} from '../../../store/useMyChecklistStore';
import { useToastStore } from '../../../store/useToastStore';
import { getClockStateOn, useTimeclockStore } from '../../../store/useTimeclockStore';
import { showApiError } from '../../../errors/showApiError';
import { isoDate } from '../../../utils/dateUtils';
import { pickImageFile, type ImageSource } from '../../../utils/pickImageFile';
import { useClockInWindow } from '../../../hooks/useClockInWindow';
import { useIsTouchDevice } from '../../../hooks/useIsTouchDevice';
import { useClockStamp } from '@/hooks/useClockStamp';
import { ClockStampDialogs } from '@/components/Dashboard/Employee/ClockStampDialogs';

/** Closes a note editor by dropping its draft. */
function withoutKey(drafts: Record<string, string>, key: string): Record<string, string> {
  const next = { ...drafts };
  delete next[key];
  return next;
}

interface TaskChecklistPanelProps {
  /** ISO date the checklist belongs to. */
  date: string;
  group: TaskChecklistGroup;
  /** Read-only for past/future days — only today's work may be confirmed here. */
  readOnly?: boolean;
}

/**
 * The employee's working surface: one tap per item. Photo-mandatory items ask for a photo
 * (camera or album) straight from the checkbox, and a note can be attached in the same step — the whole
 * confirmation is a single request either way.
 */
export function TaskChecklistPanel({ date, group, readOnly = false }: TaskChecklistPanelProps) {
  const { t } = useTranslation('dashboard');
  const { completeItem, uncompleteItem, openPhoto, pendingItemId } = useMyChecklistStore();
  const { addToast } = useToastStore();
  // FACF-113: today's items can only be ticked off while clocked in — the backend refuses
  // otherwise (planning.timeClock.notClockedIn). Past days stay open for catching up.
  const isToday = date === isoDate(new Date());
  const clockState = useTimeclockStore((s) => getClockStateOn(s.todayEntry, date));
  const clockBusy = useTimeclockStore((s) => s.loading);
  const clockInWindow = useClockInWindow();
  // Clock-in asks for the work location and the vehicle of the day first.
  const clockStamp = useClockStamp();
  const needsClockIn = !readOnly && isToday && clockState !== 'in' && clockState !== 'break';
  // FACF-113: a blocking task of today (e.g. the vehicle check) must be ticked off first.
  const blocker = useMyChecklistStore((s) =>
    !readOnly && isToday ? openBlockerFor(s, date, group.taskId) : null,
  );
  const isOpenBlocker =
    !readOnly && isToday && group.isBlocking === true && (group.items ?? []).some((i) => !i.isCompleted);
  /** Note being typed, per checklist item — sent together with the confirmation. */
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const items = group.items ?? [];
  if (items.length === 0) return null;

  const confirm = async (item: ChecklistItemState, photo?: File) => {
    const id = item.checklistItemId;
    if (!id || !group.taskId) return;
    try {
      await completeItem({
        date,
        taskId: group.taskId,
        checklistItemId: id,
        note: noteDrafts[id]?.trim() || undefined,
        photo,
      });
      setNoteDrafts((d) => withoutKey(d, id));
    } catch (error) {
      showApiError(error);
    }
  };

  const revoke = async (item: ChecklistItemState) => {
    if (!item.completionId) return;
    try {
      await uncompleteItem(date, item.completionId);
    } catch (error) {
      showApiError(error);
    }
  };

  const clockIn = async () => {
    try {
      await clockStamp.stamp('clockIn');
    } catch {
      // The store has already surfaced the localized error (header widget toast).
    }
  };

  const viewPhoto = async (documentId: string) => {
    try {
      await openPhoto(documentId);
    } catch {
      addToast(t('checklist.errors.photoFailed'), 'error');
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-border-subtle bg-surface-muted dark:bg-slate-800/40 dark:border-slate-700 divide-y divide-border-subtle dark:divide-slate-700/60">
      {needsClockIn && (
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 bg-amber-50 dark:bg-amber-900/10">
          <span className="flex-1 min-w-0 text-[12px] text-amber-800 dark:text-amber-300">
            {t('checklist.clockInRequired')}
          </span>
          <button
            type="button"
            onClick={clockIn}
            disabled={clockBusy || clockStamp.preparing || !clockInWindow.allowed}
            className="inline-flex items-center gap-1 rounded-md bg-[#00A871] px-2.5 py-1.5 text-[12px] font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LogIn className="w-3.5 h-3.5" aria-hidden />
            {t('timeclock.clockInNow')}
          </button>
        </div>
      )}
      {!needsClockIn && blocker && (
        <div className="flex items-center gap-2 px-2 py-2 bg-amber-50 dark:bg-amber-900/10 text-[12px] text-amber-800 dark:text-amber-300">
          <Lock className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
          <span className="min-w-0">{t('checklist.blockedBy', { task: blocker.taskName })}</span>
        </div>
      )}
      {!needsClockIn && isOpenBlocker && (
        <div className="px-2 py-2 text-[12px] font-medium text-slate-700 dark:text-slate-300">
          {t('checklist.blockingFirst')}
        </div>
      )}
      {items.map((item) => (
        <ChecklistRow
          key={item.checklistItemId}
          item={item}
          readOnly={readOnly || needsClockIn || blocker !== null}
          busy={pendingItemId !== null && (
            pendingItemId === item.checklistItemId || pendingItemId === item.completionId
          )}
          noteDraft={noteDrafts[item.checklistItemId ?? ''] ?? null}
          onNoteDraftChange={(value) =>
            setNoteDrafts((d) => ({ ...d, [item.checklistItemId ?? '']: value }))
          }
          onNoteDraftCancel={() =>
            setNoteDrafts((d) => withoutKey(d, item.checklistItemId ?? ''))
          }
          onConfirm={(photo) => confirm(item, photo)}
          onRevoke={() => revoke(item)}
          onViewPhoto={viewPhoto}
        />
      ))}
      <ClockStampDialogs clockStamp={clockStamp} busy={clockBusy} />
    </div>
  );
}

interface ChecklistRowProps {
  item: ChecklistItemState;
  readOnly: boolean;
  busy: boolean;
  /** `null` when no note editor is open for this item. */
  noteDraft: string | null;
  onNoteDraftChange: (value: string) => void;
  onNoteDraftCancel: () => void;
  onConfirm: (photo?: File) => void;
  onRevoke: () => void;
  onViewPhoto: (documentId: string) => void;
}

function ChecklistRow({
  item,
  readOnly,
  busy,
  noteDraft,
  onNoteDraftChange,
  onNoteDraftCancel,
  onConfirm,
  onRevoke,
  onViewPhoto,
}: ChecklistRowProps) {
  const { t } = useTranslation('dashboard');
  const done = item.isCompleted ?? false;
  const needsPhoto = (item.requiresPhoto ?? false) && !done;

  const touch = useIsTouchDevice();
  /** Camera-or-album choice, shown below the camera icon before any picker opens. */
  const [photoChoiceOpen, setPhotoChoiceOpen] = useState(false);

  const pickPhoto = (source: ImageSource) => {
    // Closed right away: a cancelled picker leaves the item exactly as it was.
    setPhotoChoiceOpen(false);
    pickImageFile(source, (file) => onConfirm(file));
  };

  // Desktops have no camera worth offering — straight to the file picker there.
  const askForPhoto = () => (touch ? setPhotoChoiceOpen(true) : pickPhoto('album'));

  const toggle = () => {
    if (readOnly || busy) return;
    if (done) {
      onRevoke();
      return;
    }
    // Cannot be confirmed without evidence — ask for the photo first.
    if (needsPhoto) {
      askForPhoto();
      return;
    }
    onConfirm();
  };

  return (
    <div className="px-2 py-1.5">
      <div className="flex items-center gap-2">
        {/* 44px tap target — thumb-sized on a phone. */}
        <button
          type="button"
          onClick={toggle}
          disabled={readOnly || busy}
          aria-pressed={done}
          aria-label={item.title ?? ''}
          className="shrink-0 -m-1 p-1 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <span
            className={`flex items-center justify-center w-6 h-6 rounded-md border transition-colors ${
              done
                ? 'bg-[#00A871] border-[#00A871] text-white'
                : 'bg-white border-slate-300 dark:bg-slate-900 dark:border-slate-600'
            }`}
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
            ) : done ? (
              <Check className="w-4 h-4" strokeWidth={3} aria-hidden />
            ) : null}
          </span>
        </button>

        <button
          type="button"
          onClick={toggle}
          disabled={readOnly || busy}
          className="flex-1 min-w-0 text-left py-1.5 disabled:cursor-not-allowed"
        >
          <span
            className={`text-[13px] sm:text-sm ${
              done
                ? 'text-ink-muted line-through dark:text-slate-500'
                : 'text-ink dark:text-slate-100'
            }`}
          >
            {item.title}
          </span>
          {item.intervalHint && (
            <span className="ml-1.5 text-[11px] text-ink-faint dark:text-slate-500">
              {item.intervalHint}
            </span>
          )}
          {needsPhoto && (
            <span className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] text-amber-700 dark:text-amber-400 align-middle">
              <Camera className="w-3 h-3" aria-hidden />
              {t('checklist.photoRequired')}
            </span>
          )}
        </button>

        {!done && !readOnly && (
          <div className="flex items-center gap-0.5 shrink-0">
            <IconAction
              label={t('checklist.addPhoto')}
              onClick={() => (photoChoiceOpen ? setPhotoChoiceOpen(false) : askForPhoto())}
              disabled={busy}
              active={photoChoiceOpen}
            >
              <Camera className="w-4 h-4" aria-hidden />
            </IconAction>
            <IconAction
              label={t('checklist.addNote')}
              onClick={() => (noteDraft === null ? onNoteDraftChange('') : onNoteDraftCancel())}
              disabled={busy}
              active={noteDraft !== null}
            >
              <MessageSquarePlus className="w-4 h-4" aria-hidden />
            </IconAction>
          </div>
        )}
      </div>

      {/* Photo source, right below the camera icon — camera for a fresh shot, album for one
          already on the phone. The camera icon closes it again. Side by side down to ~360px:
          below 400px the button icons drop out, and only narrower screens wrap. */}
      {photoChoiceOpen && touch && !done && !readOnly && (
        <div className="mt-1 flex flex-wrap justify-end gap-1.5">
          <button
            type="button"
            onClick={() => pickPhoto('album')}
            disabled={busy}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 bg-white px-2 py-2 text-[12px] text-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-600 disabled:opacity-50"
          >
            <Images className="hidden min-[400px]:block w-3.5 h-3.5" aria-hidden />
            {t('checklist.chooseFromAlbum')}
          </button>
          <button
            type="button"
            onClick={() => pickPhoto('camera')}
            disabled={busy}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 bg-white px-2 py-2 text-[12px] text-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-600 disabled:opacity-50"
          >
            <Camera className="hidden min-[400px]:block w-3.5 h-3.5" aria-hidden />
            {t('checklist.takePhoto')}
          </button>
        </div>
      )}

      {/* Note editor — confirming here submits item and note in one request. */}
      {noteDraft !== null && !done && !readOnly && (
        <div className="mt-1.5 ml-8 space-y-1.5">
          <textarea
            value={noteDraft}
            onChange={(e) => onNoteDraftChange(e.target.value.slice(0, CHECKLIST_NOTE_MAX))}
            rows={2}
            autoFocus
            placeholder={t('checklist.notePlaceholder')}
            className="w-full px-2 py-1.5 text-[13px] rounded-md border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onConfirm()}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-[#00A871] px-2.5 py-1.5 text-[12px] font-medium text-white disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" aria-hidden />
              {t('checklist.confirmWithNote')}
            </button>
            <button
              type="button"
              onClick={askForPhoto}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-[12px] text-slate-700 dark:text-slate-200 dark:border-slate-600 disabled:opacity-50"
            >
              <Camera className="w-3.5 h-3.5" aria-hidden />
              {t('checklist.withPhoto')}
            </button>
            <button
              type="button"
              onClick={onNoteDraftCancel}
              className="text-[12px] text-ink-faint hover:text-ink-muted dark:text-slate-500"
            >
              <X className="w-3.5 h-3.5" aria-hidden />
            </button>
          </div>
        </div>
      )}

      {/* Confirmed state: time, note and photo are the evidence. */}
      {done && (
        <div className="ml-8 mt-0.5 flex items-center gap-3 flex-wrap">
          {item.completedAt && (
            <span className="text-[11px] font-mono-num text-ink-faint dark:text-slate-500">
              {new Date(item.completedAt).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
          {item.photoDocumentId && (
            <button
              type="button"
              onClick={() => onViewPhoto(item.photoDocumentId!)}
              className="inline-flex items-center gap-1 text-[11px] text-[#00A871] hover:underline"
            >
              <Image className="w-3 h-3" aria-hidden />
              {t('checklist.viewPhoto')}
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={onRevoke}
              disabled={busy}
              className="inline-flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink-muted dark:text-slate-500"
            >
              <Undo2 className="w-3 h-3" aria-hidden />
              {t('checklist.undo')}
            </button>
          )}
          {item.note && (
            <span className="w-full text-[11px] text-ink-muted dark:text-slate-400 italic">
              {item.note}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function IconAction({
  label,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors disabled:opacity-50 ${
        active
          ? 'bg-[#00A871]/10 text-[#00A871]'
          : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
      }`}
    >
      {children}
    </button>
  );
}
